import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    Package,
    PackageCheck,
    AlertTriangle,
    Eye,
    Users01,
    UserPlus01,
    Globe02,
    Plus
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import { collaborationService } from '@/data/mock-collaborations';
import { teamService } from '@/data/mock-team';
import { orderService } from '@/data/mock-orders';
import { activityService } from '@/data/mock-activities';
import { accountService } from '@/data/mock-accounts';
import type { Container, ContainerStats, ContainerStatus, RiskLevel, DocumentStatus, Commodity } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import WorldRoutesMapPreview from '@/components/application/map/world-routes-map-preview';
import { storage } from '@/lib/local-storage';
import {
    aggregateCommodityData,
    aggregateOriginData,
    aggregatePortData,
    translateCommodity,
    mapCommodityDisplayToValue,
    mapPortDisplayToValue,
    mapOriginDisplayToValue
} from '@/utils/data-extractors';
import { DialogTrigger, Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { TextArea } from '@/components/base/textarea/textarea';
import { Select } from '@/components/base/select/select';
import { DatePicker } from '@/components/application/date-picker/date-picker';
import { Label } from '@/components/base/input/label';
import { getWeekNumber, getCurrentYear, type WeekData } from '@/utils/week-utils';
import { today, getLocalTimeZone, CalendarDate } from '@internationalized/date';
import { WeeklyTargetsChart } from '@/components/application/calendar/weekly-targets-chart';

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    trend?: { value: number; isPositive: boolean };
    vsLastMonthText?: string;
}

const StatCard = ({ title, value, icon: Icon, color, trend, vsLastMonthText }: StatCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50'
    };

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <div className="flex items-center">
                <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
                    <Icon className="size-6" />
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-tertiary">{title}</p>
                    <p className="text-2xl font-semibold text-primary">{value}</p>
                    {trend && vsLastMonthText && (
                        <p className={`text-sm ${trend.isPositive ? 'text-success-600' : 'text-error-600'}`}>
                            {trend.isPositive ? '+' : ''}{trend.value}% {vsLastMonthText}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ReceiverControlHubPage = () => {
    const { t } = useTranslation(['pages:dashboard', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const navigate = useNavigate();
    const [stats, setStats] = useState<ContainerStats>({
        total: 0,
        inTransit: 0,
        delivered: 0,
        delayed: 0,
        docIssues: 0,
        activeAlerts: 0,
        highRisk: 0,
    });
    const [recentContainers, setRecentContainers] = useState<Container[]>([]);
    const [collaborationStats, setCollaborationStats] = useState({ total: 0, active: 0, pending: 0 });
    const [teamStats, setTeamStats] = useState({ total: 0, active: 0 });
    const [weeklyData, setWeeklyData] = useState<WeekData[]>([]);
    const [selectedYear] = useState(getCurrentYear());
    const [isLoading, setIsLoading] = useState(true);
    const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
    const [newOrderForm, setNewOrderForm] = useState({
        shipperId: '',
        targetDate: null as CalendarDate | null,
        packingListRequested: '',
        notes: ''
    });
    const [availableShippers, setAvailableShippers] = useState<Array<{ id: string; label: string }>>([]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = storage.getItem('accountId') || '2';

            // Load container stats (filtered by receiverId)
            const receiverContainers = containerService.getAll().filter(c => c.receiverId === accountId);
            const receiverStats: ContainerStats = {
                total: receiverContainers.length,
                inTransit: receiverContainers.filter(c => c.status === 'In Transit').length,
                delivered: receiverContainers.filter(c => c.status === 'Delivered').length,
                delayed: receiverContainers.filter(c => c.status === 'Delayed').length,
                docIssues: receiverContainers.filter(c => c.documentStatus === 'Pending').length,
                activeAlerts: 0, // Not implemented yet
                highRisk: receiverContainers.filter(c => c.riskLevel === 'High' || c.riskLevel === 'Critical').length,
            };
            setStats(receiverStats);

            // Load recent containers
            const recent = containerService.getAll()
                .filter(c => c.receiverId === accountId)
                .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                .slice(0, 5);
            setRecentContainers(recent);

            // Load collaboration stats
            const allCollaborations = collaborationService.getAll();
            const receiverCollaborations = allCollaborations.filter(c => c.receiverId === accountId);
            const receiverCollabStats = {
                total: receiverCollaborations.length,
                active: receiverCollaborations.filter(c => c.status === 'Active').length,
                pending: receiverCollaborations.filter(c => c.status === 'Pending').length,
            };
            setCollaborationStats(receiverCollabStats);

            // Load team stats
            const teamStatsData = teamService.getStats(accountId);
            setTeamStats(teamStatsData);

            // Load weekly data for chart
            const weeklyDataForYear = containerService.getYearlyWeekData(accountId, selectedYear);
            setWeeklyData(weeklyDataForYear);

            setIsLoading(false);
        };
        loadData();

        // Load available shippers for order form
        const accountId = storage.getItem('accountId') || '2';
        const collaborations = collaborationService.getByReceiverId(accountId);
        const activeCollaborations = collaborations.filter(c => c.status === 'Active');
        const shipperAccounts = accountService.getAll().filter(acc => acc.userType === 'SHIPPER');
        const shippers = activeCollaborations.map(collab => {
            const shipper = shipperAccounts.find(acc => acc.id === collab.shipperId);
            return shipper ? { id: shipper.id, label: shipper.legalName } : null;
        }).filter(Boolean) as Array<{ id: string; label: string }>;
        setAvailableShippers(shippers);
    }, [selectedYear]);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Calcola distribuzioni per i grafici
    const distributionData = useMemo(() => {
        const accountId = storage.getItem('accountId') || '2';
        const userContainers = containerService.getAll().filter(c => c.receiverId === accountId);
        const total = userContainers.length;

        // Risk Levels
        const riskDistribution = userContainers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        // Container Status
        const statusDistribution = userContainers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const statusData = [
            { label: translateContainerStatus('In Transit'), value: statusDistribution['In Transit'] || 0, color: getColorHex('brand') },
            { label: translateContainerStatus('Delivered'), value: statusDistribution.Delivered || 0, color: getColorHex('success') },
            { label: translateContainerStatus('Delayed'), value: statusDistribution.Delayed || 0, color: getColorHex('error') },
            { label: translateContainerStatus('At Port'), value: statusDistribution['At Port'] || 0, color: getColorHex('warning') },
        ];

        // Document Status
        const documentDistribution = userContainers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        // Commodity Distribution
        const commodityDistribution = aggregateCommodityData(userContainers);
        const commodityColors = ['#6938ef', '#079485', '#dc6803', '#d92d20', '#6b7280', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#f97316'];
        const commodityData = Object.entries(commodityDistribution).map(([commodity, count], index) => ({
            label: translateCommodity(commodity as Commodity, 'it'),
            value: count,
            color: commodityColors[index % commodityColors.length]
        }));

        // Origin Countries Distribution
        const originDistribution = aggregateOriginData(userContainers);
        const originData = Object.entries(originDistribution).map(([country, count], index) => ({
            label: country,
            value: count,
            color: commodityColors[index % commodityColors.length]
        }));

        // Arrival Ports Distribution
        const portDistribution = aggregatePortData(userContainers);
        const portData = Object.entries(portDistribution).map(([port, count], index) => ({
            label: port,
            value: count,
            color: commodityColors[index % commodityColors.length]
        }));

        return { riskData, statusData, documentData, commodityData, originData, portData, total };
    }, [stats.total, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    // Click handlers for chart navigation
    const handleCommodityClick = (item: { label: string; value: number; color: string }) => {
        const commodityValue = mapCommodityDisplayToValue(item.label, 'it');
        if (commodityValue) {
            navigate(`/receiver/containers?commodity=${commodityValue}`);
        }
    };

    const handleOriginClick = (item: { label: string; value: number; color: string }) => {
        const originValue = mapOriginDisplayToValue(item.label);
        navigate(`/receiver/containers?origin=${encodeURIComponent(originValue)}`);
    };

    const handlePortClick = (item: { label: string; value: number; color: string }) => {
        const portValue = mapPortDisplayToValue(item.label);
        navigate(`/receiver/containers?arrivalPort=${encodeURIComponent(portValue)}`);
    };

    const handleCreateOrder = async () => {
        if (!newOrderForm.shipperId || !newOrderForm.targetDate || !newOrderForm.packingListRequested.trim()) {
            return;
        }

        const accountId = storage.getItem('accountId') || '2';
        const targetDate = newOrderForm.targetDate.toDate(getLocalTimeZone());
        const targetWeek = getWeekNumber(targetDate);
        const targetYear = targetDate.getFullYear();

        const newOrder = orderService.create({
            receiverId: accountId,
            shipperId: newOrderForm.shipperId,
            targetWeek,
            targetYear,
            packingList: {
                requested: newOrderForm.packingListRequested
            },
            notes: newOrderForm.notes || undefined
        });

        // Create activity for shipper
        const receiverAccount = accountService.getById(accountId);
        const shipperAccount = accountService.getById(newOrderForm.shipperId);
        if (receiverAccount && shipperAccount) {
            activityService.add({
                id: `order-${newOrder.id}`,
                audience: 'shipper',
                type: 'new_order_request',
                title: `Nuova richiesta ordine da ${receiverAccount.legalName}`,
                description: `Richiesta ordine con target settimana ${targetWeek}/${targetYear}. Packing list richiesta con dettagli completi.`,
                timestamp: new Date(),
                isRead: false,
                actions: [
                    { label: 'Vedi dettaglio ordine', href: `/shipper/orders/${newOrder.id}` }
                ],
                metadata: {
                    orderId: newOrder.id,
                    receiverId: accountId,
                    shipperId: newOrderForm.shipperId
                }
            });
        }

        // Reset form and close modal
        setNewOrderForm({
            shipperId: '',
            targetDate: null,
            packingListRequested: '',
            notes: ''
        });
        setIsNewOrderModalOpen(false);
        navigate(`/receiver/orders/${newOrder.id}`);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('receiver.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('controlHub.title', { ns: 'common' })}</h1>
                    <p className="text-tertiary mt-1">
                        {t('receiver.subtitle', { ns: 'pages:dashboard' })}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={() => setIsNewOrderModalOpen(true)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        iconLeading={
                            <Plus className="size-5" />
                        }
                    >
                        {t('receiver.newOrderRequest')}
                    </Button>
                    <Link
                        to="/receiver/shippers"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Users01 className="size-5" />
                        {t('receiver.manageShippers')}
                    </Link>
                    <Link
                        to="/receiver/containers"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Eye className="size-5" />
                        {t('receiver.viewContainers')}
                    </Link>
                </div>
            </div>

            {/* Stats Cards - Container */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title={t('receiver.receivedContainers')}
                    value={stats.total}
                    icon={Package}
                    color="brand"
                    trend={{ value: 15, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('receiver.stats.inTransit')}
                    value={stats.inTransit}
                    icon={PackageCheck}
                    color="success"
                    trend={{ value: 8, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('receiver.stats.delivered')}
                    value={stats.delivered}
                    icon={PackageCheck}
                    color="success"
                    trend={{ value: 22, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('receiver.stats.pendingDocuments')}
                    value={stats.docIssues}
                    icon={AlertTriangle}
                    color="warning"
                    trend={{ value: 5, isPositive: false }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
            </div>

            {/* Additional Stats Row */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <StatCard
                    title={t('receiver.stats.highRisk')}
                    value={stats.highRisk}
                    icon={AlertTriangle}
                    color="error"
                />
                <StatCard
                    title={t('receiver.activeShippers')}
                    value={collaborationStats.active}
                    icon={Users01}
                    color="brand"
                />
                <StatCard
                    title={t('receiver.team.title')}
                    value={teamStats.active}
                    icon={UserPlus01}
                    color="success"
                />
            </div>

            {/* Weekly Targets Chart */}
            {weeklyData.length > 0 && (
                <WeeklyTargetsChart
                    weeklyData={weeklyData}
                    receiverId={storage.getItem('accountId') || '2'}
                    year={selectedYear}
                    type="containers"
                />
            )}

            {/* Distribution Charts */}
            <div className="space-y-6">
                {/* Charts Row */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <DistributionChart
                        title={t('receiver.charts.commodityDistribution')}
                        data={distributionData.commodityData}
                        total={distributionData.total}
                        onItemClick={handleCommodityClick}
                    />
                    <DistributionChart
                        title={t('receiver.charts.originCountries')}
                        data={distributionData.originData}
                        total={distributionData.total}
                        onItemClick={handleOriginClick}
                    />
                    <DistributionChart
                        title={t('receiver.charts.arrivalPorts')}
                        data={distributionData.portData}
                        total={distributionData.total}
                        onItemClick={handlePortClick}
                    />
                </div>
            </div>

            {/* World Routes Map Preview (Receiver) */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-primary">{t('worldMap.title', { ns: 'pages:containers' })}</h3>
                        <p className="text-sm text-tertiary">{t('worldMap.updateInfo', { ns: 'pages:containers' })}</p>
                    </div>
                    <Link
                        to="/receiver/containers/world-map"
                        className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Globe02 className="size-4" />
                        {t('list.viewMap', { ns: 'pages:containers' })}
                    </Link>
                </div>
                <WorldRoutesMapPreview height={420} role="receiver" />
            </div>

            {/* Recent Containers */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('receiver.recentContainers.title')}
                    description={t('receiver.recentContainers.description')}
                    contentTrailing={
                        <div className="flex items-center gap-2">
                            <Link
                                to="/receiver/containers/world-map"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Globe02 className="size-4" />
                                {t('list.viewMap', { ns: 'pages:containers' })}
                            </Link>
                            <Link
                                to="/receiver/containers"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Eye className="size-4" />
                                {t('receiver.recentContainers.viewAll')}
                            </Link>
                        </div>
                    }
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('receiver.recentContainers.headers.containerId')} isRowHeader />
                        <Table.Head label={t('receiver.recentContainers.headers.shipper')} />
                        <Table.Head label={t('receiver.recentContainers.headers.route')} />
                        <Table.Head label={t('receiver.recentContainers.headers.progress')} />
                        <Table.Head label={t('receiver.recentContainers.headers.docStatus')} />
                        <Table.Head label={t('receiver.recentContainers.headers.riskLevel')} />
                    </Table.Header>
                    <Table.Body>
                        {recentContainers.map((container) => (
                            <Table.Row key={container.id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{container.containerNumber}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-tertiary">
                                        {container.shipperId} {/* In real app, would show shipper name */}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-tertiary">
                                        {container.route.from} → {container.route.to}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <ProgressBar
                                            value={container.progress}
                                            min={0}
                                            max={100}
                                        />
                                        <span className="text-sm text-tertiary">{container.progress}%</span>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                        {translateDocumentStatus(container.documentStatus)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                        {translateRiskLevel(container.riskLevel)}
                                    </Badge>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* New Order Modal */}
            <DialogTrigger isOpen={isNewOrderModalOpen} onOpenChange={setIsNewOrderModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {t('receiver.newOrderModal.title')}
                                </h2>

                                <div className="space-y-4">
                                    <Select
                                        label={t('receiver.newOrderModal.shipper')}
                                        items={availableShippers}
                                        selectedKey={newOrderForm.shipperId}
                                        onSelectionChange={(key) => setNewOrderForm({ ...newOrderForm, shipperId: key as string })}
                                        isRequired
                                        size="md"
                                        placeholder={t('receiver.newOrderModal.shipperPlaceholder')}
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <div>
                                        <Label isRequired>{t('receiver.newOrderModal.targetWeek')}</Label>
                                        <DatePicker
                                            value={newOrderForm.targetDate}
                                            onChange={(date) => setNewOrderForm({ ...newOrderForm, targetDate: date as CalendarDate | null })}
                                            minValue={today(getLocalTimeZone())}
                                        />
                                    </div>

                                    <TextArea
                                        label={t('receiver.newOrderModal.packingList')}
                                        placeholder={t('receiver.newOrderModal.packingListPlaceholder')}
                                        value={newOrderForm.packingListRequested}
                                        onChange={(value) => setNewOrderForm({ ...newOrderForm, packingListRequested: value })}
                                        isRequired
                                        rows={4}
                                    />

                                    <TextArea
                                        label={t('receiver.newOrderModal.notes')}
                                        placeholder={t('receiver.newOrderModal.notesPlaceholder')}
                                        value={newOrderForm.notes}
                                        onChange={(value) => setNewOrderForm({ ...newOrderForm, notes: value })}
                                        rows={3}
                                    />
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => setIsNewOrderModalOpen(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('common.cancel', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        onClick={handleCreateOrder}
                                        color="primary"
                                        size="md"
                                        isDisabled={!newOrderForm.shipperId || !newOrderForm.targetDate || !newOrderForm.packingListRequested.trim()}
                                    >
                                        {t('receiver.newOrderModal.submit')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};

